import React, { useState } from "react"
import { Button } from "components/button"
import { useMutation, useQueryClient } from "react-query"
import Axios from "axios"
import { useSnackbarContext } from "components/snackbar"
import { MdCloudDownload } from "react-icons/md"
import { useTranslation } from "react-i18next"

const FilesExport = () => {
  const { t } = useTranslation()
  const tBase = "views.admin.export.components.Files"
  const tr = (key: string) => t(`${tBase}.${key}`)

  const { showSnackbar } = useSnackbarContext()
  const queryClient = useQueryClient()

  const [fromDate, setFromDate] = useState<string>("")
  const [toDate, setToDate] = useState<string>("")

  const exportAllFiles = useMutation(
    async () => {
      const response = await Axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/files/export-all`,
        {
          from: fromDate,
          to: toDate
        }
      )
      return response.data
    },
    {
      onSuccess: () => {
        showSnackbar(
          tr("Export requested! You will receive an email when the export is ready.")
        )
        // Immediately refetch export jobs to show the new export
        queryClient.invalidateQueries("export-jobs")
      },
      onError: (error) => {
        console.error(error)
        showSnackbar(tr("Failed to request export"))
      }
    }
  )

  const handleExport = () => {
    exportAllFiles.mutate()
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold mb-4">{tr("Export All Company Files")}</h3>
      <p className="text-gray-600 mb-6">
        {tr("Export all files uploaded to your company account. Select a date range to filter the files by upload date. You will receive an email notification when the export is ready for download.")}
      </p>

      <div className="space-y-4">
        <div className="flex gap-4 items-end">
          <div className="flex-1 max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tr("From Date")}
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex-1 max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tr("To Date")}
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <Button
              primary
              isLoading={exportAllFiles.isLoading}
              onClick={handleExport}
            >
              <MdCloudDownload style={{ marginRight: "8px", fontSize: "20px" }} />
              {tr("Request Export")}
            </Button>
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-50 rounded-md">
          <h4 className="text-sm font-medium text-blue-800 mb-2">
            {tr("How it works")}
          </h4>
          <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
            <li>{tr("Click 'Request Export' to start the export process")}</li>
            <li>{tr("You will receive a notification that your export has been requested")}</li>
            <li>{tr("Once complete, you will receive an email with a download link")}</li>
            <li>{tr("Large exports may take several minutes to process")}</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default FilesExport
